import { Download, Visibility } from "@mui/icons-material";
import * as MUI from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import GoBack from "../../../components/buttons/GoBack";
import SimpleAlert from "../../../components/display/SimpleAlert";
import NotFoundFullScreenMessage from "../../../components/fullScreenMessage/NotFoundFullScreenMessage";
import Skeletons from "../../../components/loader/Skeletons";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import TopNavigator from "../../../components/wrappers/TopNavigator";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { SnackbarContext } from "../../../contexts/Snackbar";
import { GENERATE_DR_LETTER } from "../../../enums/ApiPaths";
import { STATUSES } from "../../../enums/AppointmentStatus";
import { CREATE_TREATMENT } from "../../../enums/PermissionTitles";
import { APPOINTMENT_GENERAL } from "../../../enums/QueryKeys";
import { APPOINTEMENTS } from "../../../enums/UrlPaths";
import { fetchAppointment } from "../../../enums/queryFunction/Fetch";
import useFileDownload from "../../../hooks/useFileDownload";
import useUser from "../../../hooks/useUser";
import { getStatusOrder } from "../../admin/appointments/components/DesktopReservations";
import AppointmentStatusSymbol from "../components/AppointmentStatusSymbol";
import DeleteAndEditAppointment from "../components/DeleteAndEditAppointment";
import Information from "../components/Information";
import RevertAppointment, {
  canRevertAppointment,
} from "../components/RevertAppointment";
import TreatmentEditing, {
  canEditTreatment,
} from "../components/TreatmentEditing";
import EditAppointment from "../components/editing/EditAppointment";
import FinishMeetingButton from "./components/FinishMeetingButton";
import { useGetSingleAppointmentDetails } from "./hooks/AppointmentDetails";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTheme } from "@emotion/react";
import ActionRow from "../../admin/clinic/components/actions/ActionRow";
import DownloadInvoice from "./components/DownloadInvoice";
import useFileViewer from "../../../hooks/useFileViewer";
import FileViewerDialog from "../../../components/display/fileViewer/FileViewerDialog";

const DOCTORS_LETTER_FILE_NAME = "doctorsLetter.pdf";

export default function AppointmentView() {
  const { id } = useParams();
  const { roles } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const previousUrl = location.state?.from;

  const { data, isLoading, error } = useQuery({
    queryKey: [APPOINTMENT_GENERAL + id],
    queryFn: () => fetchAppointment(id, "General"),
    staleTime: Infinity,
  });

  const status = data?.appointment?.status;
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const [editing, setEditing] = React.useState(false);
  const { handleSnackbar } = React.useContext(SnackbarContext);

  const details = useGetSingleAppointmentDetails(data?.appointment);

  function handleEditing() {
    setEditing((prevState) => !prevState);
  }

  const finishMeetingCondition =
    isPermissionAllowed(CREATE_TREATMENT) &&
    (data?.appointment?.status == STATUSES.ACTIVE ||
      data?.appointment?.status == STATUSES.RESOLVED) &&
    !roles.master &&
    !roles.user;

  const goBack = !roles.user ? previousUrl : APPOINTEMENTS;

  if (isLoading) {
    return (
      <PageContentWrapper>
        <MUI.Stack spacing={2}>
          <Skeletons
            numOfSkeletons={3}
            md={6}
            spacing={2}
            height={50}
            grid={false}
          />
          <Skeletons numOfSkeletons={1} md={6} height={500} grid={false} />
        </MUI.Stack>
      </PageContentWrapper>
    );
  } else if (error) {
    if (String(error?.response?.data).toUpperCase() === String("FORBIDDEN")) {
      navigate(-1);
      return;
    }
    return (
      <PageContentWrapper>
        <NotFoundFullScreenMessage />
      </PageContentWrapper>
    );
  }

  return (
    <PageContentWrapper>
      <MUI.Paper
        data-testid="appointment.details.loaded"
        sx={{
          p: 1,
          mb: 2,
        }}
      >
        <TopNavigator mb={0}>
          <GoBack url={goBack} testId={"appointment.details.back-button"} />

          <MUI.Stack
            alignItems={"center"}
            direction={"row"}
            justifyContent={"center"}
            spacing={1}
          >
            <AppointmentStatusSymbol
              currentAppointment={data?.appointment}
              testId="appointment.details.status-symbol"
            />

            {!roles.master ? (
              <DeleteAndEditAppointment
                handleSnackbar={handleSnackbar}
                appointment={data?.appointment}
                handleEditing={handleEditing}
              />
            ) : null}

            <AppointmentActionsDialog
              finishMeetingCondition={finishMeetingCondition}
              data={data}
            />
          </MUI.Stack>
        </TopNavigator>
      </MUI.Paper>
      <SimpleAlert
        mb={2}
        message={"AppointmentNeedsToBeResolvedDesc"}
        severity={"warning"}
        show={roles.adminOrEmployee && getStatusOrder(data?.appointment) == 3}
        testId={"appointment.details.needs-resolved-alert"}
      />
      <SimpleAlert
        mb={2}
        message={t("DeceasedPetAppointmentAlert")}
        severity={"warning"}
        show={!!data?.appointment?.pet && data?.appointment?.pet?.deceased}
        testId={"appointment.details.deceased-alert"}
      />
      <SimpleAlert
        mb={2}
        message={t("NoPetAppointmentAlert")}
        severity={"warning"}
        show={!data?.appointment?.pet}
      />
      {editing ? (
        // BACK SIDE (edit form)
        <EditAppointment
          handleEditing={handleEditing}
          allPets={data?.usersPets}
          appointmentProp={data?.appointment}
        />
      ) : (
        // FRONT SIDE (information)
        <Information
          object_info={details}
          status={status}
          appointmentClinicId={data?.appointment?.clinic?.id}
        />
      )}
    </PageContentWrapper>
  );
}

function AppointmentActionsDialog({ finishMeetingCondition, data }) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const [openDialog, setOpenDialog] = React.useState(false);
  const theme = useTheme();
  function handleDialog() {
    setOpenDialog((prev) => !prev);
  }

  // Mirrors the show conditions of each action below, the button is hidden when none apply
  const hasActions =
    finishMeetingCondition ||
    canRevertAppointment({
      status: data?.appointment?.status,
      isConfirmed: data?.appointment?.confirmedAt,
      roles,
    }) ||
    !!data?.appointment?.treatment ||
    canEditTreatment(data?.appointment, roles) ||
    data?.invoices?.length > 0;

  if (!hasActions) {
    return null;
  }

  return (
    <Fragment>
      <MUI.IconButton
        title={t("Actions")}
        onClick={() => handleDialog()}
        data-testid={"appointment.details.actions-button"}
        sx={{
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: MUI.alpha(theme.palette.primary.main, 0.08),
            transform: "scale(1.05)",
          },
        }}
      >
        <MoreVertIcon />
      </MUI.IconButton>
      <MUI.Dialog
        open={openDialog}
        onClose={() => handleDialog()}
        fullWidth={true}
        maxWidth={"xs"}
        data-testid={"appointment.details.actions-dialog"}
        PaperProps={{
          sx: {
            borderRadius: 8,
          },
        }}
      >
        <AppointmentActionsDialogHeader onClose={() => handleDialog()} />

        <MUI.Divider sx={{ opacity: 0.6 }} />

        <MUI.DialogContent sx={{ p: 0 }}>
          <Fragment>
            <FinishMeetingButton
              finishMeetingCondition={finishMeetingCondition}
            />
            <RevertAppointment
              status={data?.appointment?.status}
              isConfirmed={data?.appointment?.confirmedAt}
            />
            {data?.appointment?.treatment ? <DoctorsLetterActionRows /> : null}

            <TreatmentEditing initValue={data?.appointment} />

            <DownloadInvoice invoicesList={data.invoices} />
          </Fragment>
        </MUI.DialogContent>
      </MUI.Dialog>
    </Fragment>
  );
}

function AppointmentActionsDialogHeader({ onClose }) {
  const { t } = useTranslation();
  return (
    <MUI.Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
      }}
    >
      <MUI.Stack direction="row" spacing={1} alignItems="center">
        <MUI.Typography variant="h6" component="h2" fontWeight={600}>
          {t("Actions")}
        </MUI.Typography>
      </MUI.Stack>
      <MUI.IconButton
        onClick={onClose}
        color="error"
        title={t("Close")}
        data-testid={"appointment.details.actions-dialog.close-button"}
      >
        <CloseIcon />
      </MUI.IconButton>
    </MUI.Box>
  );
}

function DoctorsLetterActionRows() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { downloadDoctorsLetter } = useFileDownload(GENERATE_DR_LETTER, id);
  const {
    viewFile,
    downloadViewerFile,
    closeViewer,
    viewerFile,
    isPathLoading,
  } = useFileViewer();
  const doctorsLetterPath = `${GENERATE_DR_LETTER}${id}`;

  return (
    <Fragment>
      <MUI.Divider />
      <ActionRow
        justButton
        icon={<Visibility />}
        label={t("ViewDoctorsLetter")}
        description={t("ViewDoctorsLetterDesc")}
        loading={isPathLoading(doctorsLetterPath)}
        onClick={() =>
          viewFile({
            path: doctorsLetterPath,
            name: DOCTORS_LETTER_FILE_NAME,
            mimeType: "application/pdf",
          })
        }
        testId={"appointment.details.view-letter-button"}
      />
      <MUI.Divider />
      <ActionRow
        justButton
        icon={<Download />}
        label={t("DownloadDoctorsLetter")}
        description={t("DownloadDoctorsLetterDesc")}
        onClick={() => downloadDoctorsLetter()}
        testId={"appointment.details.download-letter-button"}
      />
      <FileViewerDialog
        file={viewerFile}
        onClose={closeViewer}
        onDownload={downloadViewerFile}
      />
    </Fragment>
  );
}
